import { useState, useEffect, useRef } from 'react';
import { providerAPI } from '../services/api';

/**
 * Custom hook for managing providers
 */
export const useProviders = (filters = {}) => {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const initialLoadDone = useRef(false);

  const fetchProviders = async () => {
    try {
      // Only show full-page loading on initial load, not filter refetches.
      // Setting loading=true on refetches causes the full component tree to
      // unmount (ProviderList returns a loading spinner), which destroys the
      // search input and loses focus on every keystroke.
      if (!initialLoadDone.current) setLoading(true);
      setError(null);
      const response = await providerAPI.getAll(filters);
      setProviders(response.data || []);
      initialLoadDone.current = true;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch providers');
      console.error('Error fetching providers:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProviders();
  }, [JSON.stringify(filters)]);

  const createProvider = async (data) => {
    try {
      const response = await providerAPI.create(data);
      setProviders([...providers, response.data]);
      return response.data;
    } catch (err) {
      throw new Error(err.response?.data?.message || 'Failed to create provider');
    }
  };

  const updateProvider = async (id, data) => {
    try {
      const response = await providerAPI.update(id, data);
      setProviders(providers.map((p) => (p.id === id ? response.data : p)));
      return response.data;
    } catch (err) {
      throw new Error(err.response?.data?.message || 'Failed to update provider');
    }
  };

  const deleteProvider = async (id) => {
    try {
      await providerAPI.delete(id);
      setProviders(providers.filter((p) => p.id !== id));
    } catch (err) {
      throw new Error(err.response?.data?.message || 'Failed to delete provider');
    }
  };

  return {
    providers,
    loading,
    error,
    refetch: fetchProviders,
    createProvider,
    updateProvider,
    deleteProvider
  };
};

/**
 * Custom hook for fetching a single provider
 */
export const useProvider = (id) => {
  const [provider, setProvider] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProvider = async () => {
      if (!id) return;

      try {
        setLoading(true);
        setError(null);
        const response = await providerAPI.getById(id);
        setProvider(response.data);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to fetch provider');
        console.error('Error fetching provider:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchProvider();
  }, [id]);

  return { provider, loading, error };
};
